import { useState, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import Rating from '@mui/material/Rating';
import { suppliers, parts, supplierRatings } from '../../services/dataClient';

const activeSuppliers = suppliers.filter((s) => s.active);

/**
 * @param {{
 *   open: boolean,
 *   order: Object|null,
 *   onClose: () => void,
 *   onSubmit: ({ supplierNames: string[], notes: string }) => void,
 * }} props
 */
export default function SupplierRFQModal({ open, order, onClose, onSubmit }) {
  const [selected, setSelected] = useState({});
  const [notes,    setNotes]    = useState('');

  const partOem = useMemo(() => {
    const p = parts.find((pt) => pt.partNumber === order?.partNumber);
    return p?.oem ?? null;
  }, [order]);

  const handleClose = () => {
    setSelected({});
    setNotes('');
    onClose();
  };

  const toggleSupplier = (supplierId) => {
    setSelected((prev) => ({ ...prev, [supplierId]: !prev[supplierId] }));
  };

  const selectedNames = activeSuppliers
    .filter((s) => selected[s.id])
    .map((s) => s.name);

  const canSubmit = selectedNames.length > 0;
  const allSelected = activeSuppliers.every((s) => selected[s.id]);

  const toggleAll = () => {
    if (allSelected) {
      setSelected({});
    } else {
      const all = {};
      activeSuppliers.forEach((s) => { all[s.id] = true; });
      setSelected(all);
    }
  };

  const handleSubmit = () => {
    onSubmit({ supplierNames: selectedNames, notes: notes.trim() });
    setSelected({});
    setNotes('');
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => { if (reason !== 'backdropClick') handleClose(); }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{ display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '1px solid var(--grey-200)',
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--grey-900)' }}>
            Request Supplier Quotes — {order?.ticketNumber}
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: '4px' }}>
            Select one or more suppliers to contact. An email will be sent requesting price and availability.
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mt: '-2px', mr: -1, ml: 1, flexShrink: 0 }}
        >
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: '12px !important', pb: 0 }}>
        {/* Supplier checklist */}
        <Box sx={{ border: '1px solid var(--grey-200)', borderRadius: '6px', overflow: 'hidden' }}>
          {/* Select All row */}
          <Box
            onClick={toggleAll}
            sx={{ display: 'flex',
              alignItems: 'center',
              px: 2,
              py: '8px',
              cursor: 'pointer',
              backgroundColor: '#f8fafc',
              borderBottom: '1px solid var(--grey-200)',
              '&:hover': { backgroundColor: '#f0faf9' },
            }}
          >
            <Checkbox
              checked={allSelected}
              indeterminate={selectedNames.length > 0 && !allSelected}
              size="small"
              disableRipple
              sx={{ p: 0, mr: '12px', flexShrink: 0, color: 'var(--grey-300)', '&.Mui-checked': { color: '#009688' }, '&.MuiCheckbox-indeterminate': { color: '#009688' } }}
              onClick={(e) => e.stopPropagation()}
              onChange={toggleAll}
            />
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--grey-700)' }}>
              {allSelected ? 'Deselect All' : 'Select All'}
            </Typography>
          </Box>

          {activeSuppliers.map((sup, i) => {
            const isChecked = Boolean(selected[sup.id]);
            const matchedRep = sup.salesReps?.find((r) => r.oem === partOem) ?? null;
            const contact = matchedRep
              ? { name: matchedRep.name, email: matchedRep.email }
              : { name: sup.contactName, email: sup.contactEmail };
            return (
              <Box key={sup.id}>
                {i > 0 && <Divider sx={{ borderColor: 'var(--grey-200)' }} />}
                <Box
                  onClick={() => toggleSupplier(sup.id)}
                  sx={{ display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: '10px',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#f8fafc' },
                    backgroundColor: isChecked ? '#f0faf9' : 'transparent',
                    transition: 'background-color 0.15s',
                  }}
                >
                  <Checkbox
                    checked={isChecked}
                    size="small"
                    disableRipple
                    sx={{ p: 0,
                      mr: '12px',
                      flexShrink: 0,
                      color: 'var(--grey-300)',
                      '&.Mui-checked': { color: '#009688' },
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleSupplier(sup.id)}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--grey-900)' }}>
                        {sup.name}
                      </Typography>
                      {matchedRep && (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '6px', py: '1px', borderRadius: '4px', backgroundColor: '#E0F2F1' }}>
                          <Typography sx={{ fontSize: '0.625rem', fontWeight: 700, color: '#00695C', letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                            {partOem}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', mt: '1px' }}>
                      {contact.name} · {contact.email}
                    </Typography>
                  </Box>
                  {(() => {
                    const ratings = supplierRatings.filter((r) => r.supplierName === sup.name);
                    if (!ratings.length) return null;
                    const avg = ratings.reduce((s, r) => s + r.rating, 0) / ratings.length;
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', flexShrink: 0, ml: 1 }}>
                        <Rating
                          value={avg}
                          readOnly
                          precision={0.5}
                          size="small"
                          sx={{ fontSize: '0.8125rem', color: 'var(--warning-dark)', '& .MuiRating-iconEmpty': { color: 'var(--grey-300)' } }}
                        />
                        <Typography sx={{ fontSize: '0.6875rem', color: 'var(--grey-400)' }}>
                          {avg.toFixed(1)}
                        </Typography>
                      </Box>
                    );
                  })()}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Notes field */}
        <TextField
          size="small"
          label="Message / Notes (optional)"
          placeholder="e.g. urgent — SLA due in 3 days, need pricing and ETA ASAP"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          multiline
          rows={3}
          sx={{ mt: 2,
            mb: 2,
            '& .MuiInputLabel-root': { fontSize: '0.875rem' },
            '& .MuiInputLabel-root.Mui-focused': { color: '#009688' },
            '& .MuiOutlinedInput-root': {
              fontSize: '0.875rem',
              '& fieldset': { borderColor: 'var(--grey-200)' },
              '&.Mui-focused fieldset': { borderColor: '#009688' },
            },
          }}
        />

      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
        <Typography
          onClick={handleClose}
          sx={{ fontSize: '0.875rem',
            color: 'var(--grey-500)',
            cursor: 'pointer',
            '&:hover': { color: 'var(--grey-700)' },
            mr: 'auto',
          }}
        >
          Cancel
        </Typography>
        <Button
          variant="contained"
          size="small"
          disabled={!canSubmit}
          onClick={handleSubmit}
          sx={{ backgroundColor: '#009688',
            '&:hover': { backgroundColor: '#00796B' },
            '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            boxShadow: 'none',
            px: 2,
          }}
        >
          Send Quote Requests{canSubmit ? ` (${selectedNames.length})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
